import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Form } from 'react-bootstrap';
import Image from "react-bootstrap/Image";
import { resolveImageUrl, resolveSwatchUrl } from '../utils/imageUrl';

/**
 * One required NHK slot. `options` is an array of color-families (each family = colors of one style).
 * When there are multiple options, user picks one product, then color + size.
 */
const KitItem = ({
  sizeIdx,
  options,
  products, // legacy single color-family
  inventory,
  setSize,
  remove,
  edit,
  currentSize,
  clearErrors,
  choiceGroups,
  onAssignChoiceGroup,
  groupTitle,
}) => {
  const apiUrl = process.env.REACT_APP_LOCALHOST_URL;
  const optionList = (options && options.length > 0)
    ? options
    : (products ? [products] : []);

  const [selectedOptionIdx, setSelectedOptionIdx] = useState(0);
  const selectedFamily = optionList[selectedOptionIdx] || optionList[0] || [];
  const [selectedProduct, setSelectedProduct] = useState(selectedFamily[0] || null);
  const [selectedItemSize, setSelectedItemSize] = useState(null);

  useEffect(() => {
    setSelectedOptionIdx(0);
  }, [optionList.map(o => o[0]?.groupId).join(',')]);

  useEffect(() => {
    const family = optionList[selectedOptionIdx] || [];
    setSelectedProduct(family[0] || null);
    setSelectedItemSize(null);
    if (setSize) setSize(sizeIdx, null);
  }, [selectedOptionIdx]);

  useEffect(() => {
    if (selectedFamily[0] && (!selectedProduct || !selectedFamily.find(p => p.productId === selectedProduct.productId))) {
      setSelectedProduct(selectedFamily[0]);
    }
  }, [selectedFamily]);

  if (!selectedProduct) return null;

  const sizeChange = (e) => {
    var itemSize = selectedProduct.sizes.find(size => size.productId == e.target.value);
    setSize(sizeIdx, itemSize ? itemSize.productId : null);
    clearErrors && clearErrors();
    setSelectedItemSize(itemSize ? itemSize.productId : "");
  };

  const formatPantsSize = (sizeName) => {
    var isFour = /^\d{4}$/.test(sizeName);
    if (isFour) {
      return sizeName.substring(0, 2) + "x" + sizeName.substring(2);
    }
    return sizeName;
  };

  const showProductColor = (newSelectedProduct) => {
    setSelectedProduct(newSelectedProduct);
    setSelectedItemSize("");
    setSize(sizeIdx, null);
  };

  const selectOption = (idx) => {
    setSelectedOptionIdx(idx);
    clearErrors && clearErrors();
  };

  const multiOption = optionList.length > 1;
  const headerTitle = groupTitle || (multiOption ? "Choose one" : selectedFamily[0]?.title);

  return (
    <Container className="mt-4" style={{ border: "1px solid #CCCCCC", padding: 10 }}>
      <div style={{ fontWeight: 600, marginBottom: 8, fontSize: '1.05em' }}>
        {headerTitle}
        {multiOption && (
          <span style={{ fontWeight: 400, color: '#666', marginLeft: 8, fontSize: '0.9em' }}>
            (pick one)
          </span>
        )}
      </div>

      {edit && choiceGroups && onAssignChoiceGroup && (
        <Form.Group className="mb-3" style={{ maxWidth: 360 }}>
          <Form.Label style={{ fontSize: '0.85em', marginBottom: 2 }}>Choice group</Form.Label>
          <Form.Select
            size="sm"
            value={selectedFamily[0]?.choiceGroupId || ""}
            onChange={(e) => {
              const nhkiId = selectedFamily[0]?.nhkiId;
              const val = e.target.value === "" ? null : parseInt(e.target.value, 10);
              onAssignChoiceGroup(nhkiId, val);
            }}
          >
            <option value="">Standalone (always included)</option>
            {choiceGroups.map((g) => (
              <option key={g.id} value={g.id}>{g.title}</option>
            ))}
          </Form.Select>
          <Form.Text className="text-muted">
            Put multiple products in the same group so the user picks only one.
          </Form.Text>
        </Form.Group>
      )}

      {multiOption && (
        <div style={{ marginBottom: 12 }}>
          {optionList.map((family, idx) => (
            <Form.Check
              key={family[0]?.groupId || idx}
              type="radio"
              name={`nhk-option-${sizeIdx}`}
              id={`nhk-option-${sizeIdx}-${idx}`}
              label={family[0]?.title || `Option ${idx + 1}`}
              checked={selectedOptionIdx === idx}
              onChange={() => selectOption(idx)}
              className="mb-1"
            />
          ))}
        </div>
      )}

      <Row>
        <Col sm={2}>
          <div style={{ display: "inline-block", position: "relative" }}>
            <Image className="modelImageImg" src={resolveImageUrl(selectedProduct.frontModelImage)} fluid />
            {(selectedProduct.logos || []).filter(logo => logo.x != null && logo.y != null && logo.width != null).map((logo, i) => (
              <img key={i} className="logo-image" src={apiUrl + logo.decoration_logo.imageUrl} width={logo.width + '%'} style={{ left: logo.x + '%', top: logo.y + '%', transform: 'rotate(' + logo.rotate + 'deg)' }} alt="" />
            ))}
          </div>
        </Col>
        <Col sm={10}>
          <div style={{ position: "relative" }}>
            {remove && edit && (
              <Button
                variant="danger"
                size="sm"
                style={{ position: "absolute", top: 0, right: 0 }}
                onClick={() => remove(selectedProduct)}
              >
                Remove from Kit
              </Button>
            )}
            {!multiOption && <div className="title">{selectedProduct.title}</div>}
            {multiOption && <div className="title" style={{ fontSize: '1em' }}>{selectedProduct.title}</div>}
            <div className="color-title">
              {selectedFamily.map(item => (
                <div style={{ marginRight: 10, display: "inline-block", cursor: 'pointer', outline: selectedProduct.productId === item.productId ? '2px solid #333' : 'none' }} key={item.colorName + '-' + item.productId} onClick={() => showProductColor(item)}>
                  {item.hexColor && item.hexColor != "" && !(item.squareImageName && item.squareImageName != "") && (
                    <div style={{ marginLeft: 10, display: "inline-block", backgroundColor: item.hexColor, width: 40, height: 40 }}></div>
                  )}
                  {item.squareImageName && item.squareImageName != "" && (
                    <img src={resolveSwatchUrl(item.squareImageName)} height={40} alt={item.colorName} />
                  )}
                </div>
              ))}
            </div>
            <div className="color-title">
              Decorations:
              <div style={{ padding: 5, marginLeft: 20 }}>
                {(selectedProduct.logos || []).map((logo, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', marginBottom: 5 }}>
                    <span style={{ fontWeight: 'bold', minWidth: 120 }}>
                      {logo.deco_location?.description || 'Unknown Location'}:
                    </span>
                    <span style={{ marginLeft: 10 }}>
                      {logo.decoration_logo.decorationName} ({logo.decoration_logo.decoType})
                    </span>
                    {logo.x == null && (
                      <span style={{ marginLeft: 10, color: '#999', fontSize: '0.8em' }}>
                        (not shown on image)
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Form.Group className="mb-3" controlId={`size-${sizeIdx}`}>
                {edit && (
                  <select
                    name="size"
                    onChange={sizeChange}
                    value={selectedItemSize || -1}
                  >
                    <option value={-1}>Select a Size</option>
                    {(selectedProduct.sizes || []).map(size => {
                      var inv = (inventory || []).find(item => item.uniqueKey == size.uniqueKey);
                      if (inv && inv.quantity == 0) {
                        return (
                          <option key={size.productId} value={size.productId} disabled>{formatPantsSize(size.sizeName)}</option>
                        );
                      }
                      if (inv && inv.quantity < 30) {
                        return (
                          <option key={size.productId} value={size.productId}>{formatPantsSize(size.sizeName) + " Low Stock: " + inv.quantity}</option>
                        );
                      }
                      return (
                        <option key={size.productId} value={size.productId}>{formatPantsSize(size.sizeName)}</option>
                      );
                    })}
                  </select>
                )}
                {!edit && (
                  <div>Size: {currentSize}</div>
                )}
              </Form.Group>
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default KitItem;
